(function () {
  const SELECTOR = "[data-kwl-cf-reviews]";

  function stars(rating) {
    const whole = Math.round(Number(rating) || 0);
    let html = "";
    for (let i = 1; i <= 5; i += 1) {
      html += i <= whole ? "&#9733;" : "&#9734;";
    }
    return html;
  }

  function escapeHtml(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  function formatDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function formatReviewerName(value) {
    const raw = String(value || "").trim().replace(/\s+/g, " ");
    if (!raw) return "Verified customer";

    const tokens = raw.split(" ").filter(Boolean);
    if (tokens.length === 1) return tokens[0];

    const fillerWords = new Set(["a", "an", "and", "as", "at", "by", "for", "from", "in", "of", "on", "sign", "the", "to", "user", "via", "with"]);
    const firstVisible = tokens.find(function (token) {
      return !fillerWords.has(token.toLowerCase());
    }) || tokens[0];
    const lastVisible = tokens[tokens.length - 1];
    const initial = (lastVisible[0] || "").toUpperCase();

    if (!initial || firstVisible.toLowerCase() === lastVisible.toLowerCase()) {
      return firstVisible;
    }

    return firstVisible + " " + initial + ".";
  }

  function reviewImageAlt(review, displayName) {
    const productTitle = String(review.productTitle || "").trim();
    if (productTitle) {
      return "Customer review photo for " + productTitle;
    }

    if (displayName) {
      return "Customer review photo from " + displayName;
    }

    return "Customer review photo";
  }

  function buildUrl(root, page) {
    const endpoint = root.dataset.endpoint;
    const mode = root.dataset.mode || "product";
    const perPage = root.dataset.perPage || (mode === "all" ? "25" : "5");
    const rating = root.dataset.rating;
    const sort = root.dataset.sort || "highest";
    const url =
      mode === "all"
        ? new URL("/v1/all-reviews", endpoint)
        : new URL("/v1/product-reviews", endpoint);

    url.searchParams.set("page", String(page));
    url.searchParams.set("per_page", perPage);

    if (mode !== "all") {
      if (root.dataset.productId) url.searchParams.set("external_id", root.dataset.productId);
      if (root.dataset.productHandle) url.searchParams.set("handle", root.dataset.productHandle);
    }

    if (rating) {
      url.searchParams.set("rating", rating);
    }

    if (mode === "all" && sort) {
      url.searchParams.set("sort", sort);
    }

    return url.toString();
  }

  function buildBulkUrl(root, page) {
    const endpoint = root.dataset.endpoint;
    const mode = root.dataset.mode || "product";
    const rating = root.dataset.rating;
    const url =
      mode === "all"
        ? new URL("/v1/all-reviews", endpoint)
        : new URL("/v1/product-reviews", endpoint);

    url.searchParams.set("page", String(page));
    url.searchParams.set("per_page", "50");

    if (mode !== "all") {
      if (root.dataset.productId) url.searchParams.set("external_id", root.dataset.productId);
      if (root.dataset.productHandle) url.searchParams.set("handle", root.dataset.productHandle);
    }

    if (rating) {
      url.searchParams.set("rating", rating);
    }

    return url.toString();
  }

  async function fetchJson(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to load reviews");
    return response.json();
  }

  async function fetchAllReviews(root) {
    if (root._allReviewsPromise) return root._allReviewsPromise;

    root._allReviewsPromise = (async function () {
      const first = await fetchJson(buildBulkUrl(root, 1));
      const totalPages = first.pagination?.totalPages || 1;
      let reviews = first.reviews || [];

      if (totalPages > 1) {
        const pages = await Promise.all(
          Array.from({ length: totalPages - 1 }, function (_, index) {
            return fetchJson(buildBulkUrl(root, index + 2));
          })
        );

        pages.forEach(function (page) {
          reviews = reviews.concat(page.reviews || []);
        });
      }

      return {
        summary: first.summary,
        product: first.product,
        reviews: reviews,
      };
    })();

    return root._allReviewsPromise;
  }

  function collectPhotoItems(reviews) {
    const seen = new Set();
    const items = [];

    reviews.forEach(function (review) {
      const displayName = formatReviewerName(review.reviewerName);

      (review.images || []).forEach(function (image) {
        if (!image.fullUrl || seen.has(image.fullUrl)) return;
        seen.add(image.fullUrl);
        items.push({
          fullUrl: image.fullUrl,
          thumbnailUrl: image.thumbnailUrl || image.fullUrl,
          alt: reviewImageAlt(review, displayName),
          reviewerName: displayName,
          createdAt: review.createdAt,
          title: review.title || "",
          body: review.body || "",
          rating: review.rating || 0,
        });
      });
    });

    return items;
  }

  function photoItemsPromise(root) {
    if (root._photoItemsPromise) return root._photoItemsPromise;

    root._photoItemsPromise = fetchAllReviews(root).then(function (allData) {
      return collectPhotoItems(allData.reviews || []);
    });

    return root._photoItemsPromise;
  }

  function sortReviews(reviews, sortValue) {
    const items = reviews.slice();

    function reviewTime(review) {
      const time = new Date(review.createdAt || 0).getTime();
      return Number.isNaN(time) ? 0 : time;
    }

    if (sortValue === "highest") {
      items.sort(function (a, b) {
        return (b.rating - a.rating) || (reviewTime(b) - reviewTime(a));
      });
      return items;
    }

    if (sortValue === "lowest") {
      items.sort(function (a, b) {
        return (a.rating - b.rating) || (reviewTime(b) - reviewTime(a));
      });
      return items;
    }

    if (sortValue === "photos") {
      return items
        .filter(function (review) {
          return review.hasMedia || (review.images && review.images.length);
        })
        .sort(function (a, b) {
          return reviewTime(b) - reviewTime(a);
        });
    }

    items.sort(function (a, b) {
      return reviewTime(b) - reviewTime(a);
    });
    return items;
  }

  async function getData(root, page) {
    const mode = root.dataset.mode || "product";
    const sortValue = root.dataset.sort || "highest";
    const perPage = Number(root.dataset.perPage || 5);

    if (mode === "all") {
      return fetchJson(buildUrl(root, page));
    }

    if (sortValue === "recent") {
      return fetchJson(buildUrl(root, page));
    }

    const allData = await fetchAllReviews(root);
    const filtered = sortReviews(allData.reviews || [], sortValue);
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const start = (page - 1) * perPage;
    const end = start + perPage;

    return {
      ok: true,
      source: allData.source || "judgeme",
      fetchedAt: new Date().toISOString(),
      product: allData.product,
      summary: allData.summary,
      pagination: {
        page: page,
        perPage: perPage,
        totalPages: totalPages,
      },
      reviews: filtered.slice(start, end),
    };
  }

  function renderSummary(root, data) {
    const total = data.summary?.reviewCount || 0;
    const sortValue = root.dataset.sort || "highest";

    return `
      <div class="kwl-cf-reviews__header">
        <div class="kwl-cf-reviews__summary-row">
          <div class="kwl-cf-reviews__summary">
            <div class="kwl-cf-reviews__score">${Number(data.summary?.averageRating || 0).toFixed(1)}</div>
            <div>
              <div class="kwl-cf-reviews__stars" aria-hidden="true">${stars(data.summary?.averageRating)}</div>
              <div class="kwl-cf-reviews__count">${total} reviews</div>
            </div>
          </div>
          <button type="button" class="button button--secondary kwl-cf-reviews__picture-button" data-photo-gallery-button>See customer pictures</button>
        </div>
        <div class="kwl-cf-reviews__sort">
          <label class="visually-hidden" for="KwlCfReviewsSort-${root.dataset.productHandle || "all"}">Sort customer reviews</label>
          <select id="KwlCfReviewsSort-${root.dataset.productHandle || "all"}" data-sort-select aria-label="Sort customer reviews">
            <option value="highest" ${sortValue === "highest" ? "selected" : ""}>Highest rated</option>
            <option value="recent" ${sortValue === "recent" ? "selected" : ""}>Most recent</option>
            <option value="lowest" ${sortValue === "lowest" ? "selected" : ""}>Lowest rated</option>
            <option value="photos" ${sortValue === "photos" ? "selected" : ""}>Pictures only</option>
          </select>
        </div>
      </div>
    `;
  }

  function renderReview(review) {
    const displayName = formatReviewerName(review.reviewerName);
    const images = (review.images || [])
      .map((image) => {
        return `
          <a class="kwl-cf-reviews__image-link" href="${escapeHtml(image.fullUrl)}" data-photo-open="${escapeHtml(image.fullUrl)}">
            <img src="${escapeHtml(image.thumbnailUrl)}" alt="${escapeHtml(reviewImageAlt(review, displayName))}" loading="lazy" width="144" height="144">
          </a>
        `;
      })
      .join("");

    return `
      <article class="kwl-cf-reviews__card">
        <div class="kwl-cf-reviews__meta">
          <div class="kwl-cf-reviews__meta-top">
            <span class="kwl-cf-reviews__stars" aria-label="${review.rating} out of 5 stars">${stars(review.rating)}</span>
            <span class="kwl-cf-reviews__author">${escapeHtml(displayName)}</span>
            ${review.verifiedBuyer ? '<span class="kwl-cf-reviews__verified">Verified buyer</span>' : ""}
          </div>
          <span class="kwl-cf-reviews__date">${escapeHtml(formatDate(review.createdAt))}</span>
        </div>
        ${review.title ? `<h3 class="kwl-cf-reviews__title">${escapeHtml(review.title)}</h3>` : ""}
        <div class="kwl-cf-reviews__body">${review.bodyHtml || escapeHtml(review.body)}</div>
        ${images ? `<div class="kwl-cf-reviews__images">${images}</div>` : ""}
      </article>
    `;
  }

  function renderPagination(root, data, page) {
    const totalPages = data.pagination?.totalPages || 1;
    if (totalPages <= 1) return "";

    return `
      <div class="kwl-cf-reviews__pagination">
        <button type="button" data-page="${page - 1}" ${page <= 1 ? "disabled" : ""}>Previous</button>
        <span>Page ${page} of ${totalPages}</span>
        <button type="button" data-page="${page + 1}" ${page >= totalPages ? "disabled" : ""}>Next</button>
      </div>
    `;
  }

  function renderEmptyState(root) {
    const allReviewsUrl = root.dataset.allReviewsUrl || "/pages/reviews";

    return `
      <div class="kwl-cf-reviews__empty">
        <p class="kwl-cf-reviews__empty-copy">No reviews on this product yet.</p>
        <a class="button button--secondary kwl-cf-reviews__empty-link" href="${escapeHtml(allReviewsUrl)}">See all store reviews</a>
      </div>
    `;
  }

  function renderPhotoModal(root, items, activeIndex) {
    const modal = root.querySelector("[data-photo-modal]");
    if (!modal) return;

    const stage = modal.querySelector("[data-photo-stage]");
    const caption = modal.querySelector("[data-photo-caption]");
    const counter = modal.querySelector("[data-photo-counter]");
    const empty = modal.querySelector("[data-photo-empty]");
    const prev = modal.querySelector("[data-photo-prev]");
    const next = modal.querySelector("[data-photo-next]");
    const thumbs = modal.querySelector("[data-photo-thumbs]");

    if (!items.length) {
      root.dataset.photoIndex = "0";
      if (stage) stage.innerHTML = "";
      if (caption) caption.innerHTML = "";
      if (counter) counter.textContent = "No customer photos yet";
      if (thumbs) thumbs.innerHTML = "";
      if (empty) empty.hidden = false;
      if (prev) prev.disabled = true;
      if (next) next.disabled = true;
      return;
    }

    const index = Math.max(0, Math.min(activeIndex, items.length - 1));
    const item = items[index];
    root.dataset.photoIndex = String(index);

    if (empty) empty.hidden = true;
    if (counter) counter.textContent = "Photo " + (index + 1) + " of " + items.length;
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= items.length - 1;

    if (stage) {
      stage.innerHTML = `<img src="${escapeHtml(item.fullUrl)}" alt="${escapeHtml(item.alt)}" loading="eager">`;
    }

    if (thumbs) {
      thumbs.innerHTML = items
        .map(function (thumb, thumbIndex) {
          return `
            <button
              type="button"
              class="kwl-cf-reviews__thumb${thumbIndex === index ? " is-active" : ""}"
              data-photo-thumb="${thumbIndex}"
              aria-label="Open customer photo ${thumbIndex + 1}"
            >
              <img src="${escapeHtml(thumb.thumbnailUrl)}" alt="${escapeHtml(thumb.alt)}" loading="lazy" width="72" height="72">
            </button>
          `;
        })
        .join("");

      thumbs.querySelectorAll("[data-photo-thumb]").forEach(function (button) {
        button.addEventListener("click", function () {
          renderPhotoModal(root, items, Number(button.getAttribute("data-photo-thumb")));
        });
      });
    }

    if (caption) {
      caption.innerHTML = `
        <div class="kwl-cf-reviews__photo-caption-meta">
          <span class="kwl-cf-reviews__stars" aria-hidden="true">${stars(item.rating)}</span>
          <span>${escapeHtml(item.reviewerName)}</span>
          <span>${escapeHtml(formatDate(item.createdAt))}</span>
        </div>
        ${item.title ? `<h3 class="kwl-cf-reviews__photo-caption-title">${escapeHtml(item.title)}</h3>` : ""}
        ${item.body ? `<p class="kwl-cf-reviews__photo-caption-body">${escapeHtml(item.body)}</p>` : ""}
      `;
    }
  }

  function closePhotoModal(root) {
    const modal = root.querySelector("[data-photo-modal]");
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove("kwl-cf-reviews-modal-open");
  }

  function openPhotoModal(root, fullUrl) {
    const modal = root.querySelector("[data-photo-modal]");
    if (!modal) return;

    const button = root.querySelector("[data-photo-gallery-button]");
    if (button) button.disabled = true;

    photoItemsPromise(root)
      .then(function (items) {
        const index = fullUrl
          ? Math.max(
              0,
              items.findIndex(function (item) {
                return item.fullUrl === fullUrl;
              })
            )
          : 0;

        renderPhotoModal(root, items, index);
        modal.hidden = false;
        document.documentElement.classList.add("kwl-cf-reviews-modal-open");
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        if (button) button.disabled = false;
      });
  }

  function bindPhotoModal(root) {
    if (root.dataset.photoModalBound === "true") return;
    root.dataset.photoModalBound = "true";

    const modal = root.querySelector("[data-photo-modal]");
    if (!modal) return;

    modal.addEventListener("click", function (event) {
      if (event.target === modal || event.target.hasAttribute("data-photo-close")) {
        closePhotoModal(root);
      }
    });

    const prev = modal.querySelector("[data-photo-prev]");
    const next = modal.querySelector("[data-photo-next]");

    if (prev) {
      prev.addEventListener("click", function () {
        photoItemsPromise(root).then(function (items) {
          renderPhotoModal(root, items, Number(root.dataset.photoIndex || 0) - 1);
        });
      });
    }

    if (next) {
      next.addEventListener("click", function () {
        photoItemsPromise(root).then(function (items) {
          renderPhotoModal(root, items, Number(root.dataset.photoIndex || 0) + 1);
        });
      });
    }

    document.addEventListener("keydown", function (event) {
      if (modal.hidden) return;
      if (event.key === "Escape") {
        closePhotoModal(root);
      } else if (event.key === "ArrowLeft" && prev && !prev.disabled) {
        prev.click();
      } else if (event.key === "ArrowRight" && next && !next.disabled) {
        next.click();
      }
    });
  }

  async function load(root, page, options) {
    const target = root.querySelector("[data-reviews-root]");
    const config = options || {};
    const shouldScroll = Boolean(config.scrollToTop);
    if (!target) return;

    target.innerHTML = '<p class="kwl-cf-reviews__status">Loading reviews...</p>';

    try {
      const data = await getData(root, page);
      const totalReviews = Number(data.summary?.reviewCount || 0);

      if ((root.dataset.mode || "product") === "product" && totalReviews <= 0) {
        target.innerHTML = renderEmptyState(root);
        return;
      }

      const reviews = (data.reviews || []).map(renderReview).join("");

      target.innerHTML = `
        ${renderSummary(root, data)}
        <div class="kwl-cf-reviews__list">
          ${reviews || '<p class="kwl-cf-reviews__status">No reviews yet.</p>'}
        </div>
        ${renderPagination(root, data, page)}
      `;

      const sortSelect = target.querySelector("[data-sort-select]");
      if (sortSelect) {
        sortSelect.addEventListener("change", function () {
          root.dataset.sort = sortSelect.value || "highest";
          load(root, 1, { scrollToTop: true });
        });
      }

      const photoButton = target.querySelector("[data-photo-gallery-button]");
      if (photoButton) {
        photoButton.addEventListener("click", function () {
          openPhotoModal(root);
        });
      }

      target.querySelectorAll("[data-page]").forEach((button) => {
        button.addEventListener("click", function (event) {
          event.preventDefault();
          const nextPage = Number(button.getAttribute("data-page"));
          if (nextPage > 0) {
            button.blur();
            load(root, nextPage, { scrollToTop: true });
          }
        });
      });

      target.querySelectorAll("[data-photo-open]").forEach((link) => {
        link.addEventListener("click", function (event) {
          event.preventDefault();
          openPhotoModal(root, link.getAttribute("data-photo-open"));
        });
      });

      if (shouldScroll) {
        requestAnimationFrame(function () {
          const firstReview = target.querySelector(".kwl-cf-reviews__card");
          const anchor = firstReview || target;
          const top = Math.max(0, window.scrollY + anchor.getBoundingClientRect().top - 12);
          window.scrollTo({ top: top, behavior: "smooth" });
        });
      }
    } catch (error) {
      target.innerHTML = '<p class="kwl-cf-reviews__status">We could not load reviews right now.</p>';
      console.error(error);
    }
  }

  function mount(root) {
    if (root.dataset.bound === "true") return;
    root.dataset.bound = "true";
    root.dataset.sort = root.dataset.sort || "highest";

    bindPhotoModal(root);

    const observer = new IntersectionObserver(
      function (entries) {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();
          load(root, 1, { scrollToTop: false });
          break;
        }
      },
      { rootMargin: "250px 0px" }
    );

    observer.observe(root);
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
